<template>
  <b-modal
    id="modal-repair-inventory-detail"
    size="xl"
    hide-footer
    lazy
    @hidden="onHidden"
  >
    <template #modal-title>
      Máy đang giữ —
      <span class="font-weight-bold">{{ productName || 'Model' }}</span>
      <span v-if="storeName" class="text-muted"> tại {{ storeName }}</span>
    </template>

    <b-alert v-if="error" variant="danger" show class="py-2">{{ error }}</b-alert>

    <div class="d-flex align-items-center mb-3">
      <b-form-checkbox v-model="includeReturned" switch @change="fetchData">
        Hiện cả máy đã trả / đã huỷ
      </b-form-checkbox>
      <span class="ml-auto text-dark">Tổng: {{ items.length }} máy</span>
    </div>

    <b-table
      class="table-vertical-center"
      :items="items"
      :fields="fields"
      :busy="loading"
      show-empty
      empty-text="Không có máy nào"
      bordered
      small
      responsive
    >
      <template #table-busy>
        <div class="text-center text-primary my-3">
          <b-spinner class="align-middle"></b-spinner>
          <strong class="ml-2">Đang tải...</strong>
        </div>
      </template>

      <template #cell(imei)="{ item }">
        <span class="font-weight-bold">{{ item.imei || '—' }}</span>
        <!-- Máy không có serial (tai nghe, tablet Wifi, máy mờ tem) được cấp mã định danh
             lúc rời "Tiếp nhận" — đánh dấu để nhân viên không tưởng đó là IMEI thật. -->
        <b-badge v-if="item.isGeneratedImei" variant="light" class="ml-1 border">
          Mã tự sinh
        </b-badge>
      </template>

      <template #cell(slip)="{ item }">
        <a
          href="javascript:void(0)"
          class="font-weight-bold"
          @click="$emit('open-slip', item.repairSlipId)"
        >
          {{ item.slipNumber || item.repairSlipId }}
        </a>
        <div class="small text-muted">{{ item.deviceOrigin }}</div>
      </template>

      <template #cell(customer)="{ item }">
        <div>{{ item.customerName || '—' }}</div>
        <div class="small text-muted">{{ item.customerPhone }}</div>
      </template>

      <template #cell(group)="{ item }">
        <b-badge :variant="groupVariant(item.group)">
          {{ item.groupName || '—' }}
        </b-badge>
        <div v-if="item.vendorPartner" class="small text-muted mt-1">
          {{ item.vendorPartner }}
        </div>
      </template>

      <template #cell(aging)="{ item }">
        <span :class="agingClass(item.agingLevel)" :title="agingTitle(item.agingLevel)">
          {{ item.agingDays }} ngày
        </span>
        <div class="small text-muted">{{ formatDateTime(item.receivedAt) }}</div>
      </template>

      <template #cell(appointmentDate)="{ item }">
        {{ formatDateDash(item.appointmentDate) || '—' }}
      </template>

      <template #cell(actions)="{ item }">
        <b-button
          variant="outline-secondary"
          size="sm"
          title="Xem thẻ kho của máy này"
          @click="$emit('open-history', item.imei)"
        >
          <i class="fas fa-history"></i>
        </b-button>
      </template>
    </b-table>
  </b-modal>
</template>

<script>
/**
 * SRS t101478 FR-03 — Chi tiết từng máy của một model tại một cửa hàng.
 *
 * Chỉ đọc. Không có nút đổi trạng thái ở đây: trạng thái máy là hình chiếu của
 * RepairSlips.status, đổi trạng thái phải làm trên phiếu sửa chữa để đi qua state machine
 * và các guard luân chuyển. Cột "Phiếu sửa chữa" là đường dẫn sang đó.
 */
import ApiService from '@/core/services/api.service';
import { extractError, formatDateTime, formatDateDash } from '@/utils/warranty-service.helpers';
import { STOCK_GROUP_META, AGING_META } from '@/utils/warranty-service.constants';

const API = 'v2/warranty-service/repair-inventory/stocks/detail';

const FIELDS = Object.freeze([
  { key: 'imei', label: 'IMEI / Mã định danh' },
  { key: 'slip', label: 'Phiếu sửa chữa' },
  { key: 'customer', label: 'Khách hàng' },
  { key: 'group', label: 'Tình trạng', tdClass: 'text-center' },
  { key: 'aging', label: 'Số ngày tồn', tdClass: 'text-center' },
  { key: 'appointmentDate', label: 'Hẹn trả', tdClass: 'text-center' },
  { key: 'actions', label: '', tdClass: 'text-center' },
]);

export default {
  name: 'RepairInventoryDetailModal',
  data() {
    return {
      items: [],
      loading: false,
      error: '',
      includeReturned: false,
      productId: null,
      productName: '',
      storeId: null,
      storeName: '',
    };
  },
  computed: {
    fields: () => FIELDS,
  },
  methods: {
    formatDateTime,
    formatDateDash,
    groupVariant(group) {
      return (STOCK_GROUP_META[group] && STOCK_GROUP_META[group].variant) || 'secondary';
    },
    agingClass(level) {
      return (AGING_META[level] && AGING_META[level].variant) || '';
    },
    agingTitle(level) {
      return (AGING_META[level] && AGING_META[level].title) || '';
    },
    /**
     * Mở modal cho một dòng tồn kho. Gọi từ component cha.
     * @param {{productId:string, productName:string, storeId:number, storeName:string}} row
     */
    open(row) {
      this.productId = row.productId;
      this.productName = row.productName;
      this.storeId = row.storeId;
      this.storeName = row.storeName;
      this.includeReturned = false;
      this.items = [];
      this.error = '';
      this.$bvModal.show('modal-repair-inventory-detail');
      this.fetchData();
    },
    onHidden() {
      this.items = [];
      this.error = '';
    },
    async fetchData() {
      if (!this.productId) return;
      this.loading = true;
      this.error = '';
      try {
        const { data } = await ApiService.query(API, {
          params: {
            productId: this.productId,
            // Bỏ trống khi xem "tất cả cửa hàng": BE tự ép phạm vi theo quyền của user.
            storeId: this.storeId || undefined,
            includeReturned: this.includeReturned ? 'true' : undefined,
          },
        });
        this.items = ((data && data.data) || {}).items || [];
      } catch (err) {
        this.error = extractError(err);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
